import classnames from 'classnames';
import FormControl, { FormControlProps } from '@mui/material/FormControl';
import InputLabel, { InputLabelProps } from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { forwardRef, useEffect, useRef } from 'react';
import { FieldRenderProps } from 'react-final-form';
import Quill from 'quill';
import { OnErrorScrollPoint } from 'components/common/on-error-scroll-point';
import { Box } from '../../layout';
import { editorContentStyle, containerStyle } from './styles';
import 'quill/dist/quill.snow.css';

interface WysiwygFieldProps extends Omit<
	FormControlProps,
	'onChange' | 'onBlur' | 'onFocus'
> {
	value: string;
	label?: string;
	placeholder?: string;
	LabelProps?: InputLabelProps;
	name: string;
	helperText?: string;
	onChange: (value: string) => void;
	onBlur: () => void;
	onFocus: () => void;
	meta: FieldRenderProps<string>['meta'];
	rows?: number;
	editorId?: string;
}

export const WysiwygField = forwardRef<Quill, WysiwygFieldProps>(
	function WysiwygField(props, ref) {
		const {
			value,
			placeholder,
			label,
			LabelProps,
			helperText,
			onFocus = () => ({}),
			onBlur = () => ({}),
			onChange,
			meta,
			rows = 2,
			editorId = 'editor',
			...rest
		} = props;

		const editorRef = useRef<Quill | null>(null);
		const textSet = useRef(false);

		useEffect(() => {
			if (editorRef.current) {
				return;
			}

			const quill = new Quill(`#${editorId}`, {
				theme: 'snow',
				placeholder,
				modules: {
					toolbar: {
						container: `#${editorId}-toolbar`,
					},
				},
			});

			quill.on('text-change', () => {
				const html = quill.root.innerHTML;
				const isEmpty =
					quill.getText().trim() === '' ||
					html === '<p><br></p>' ||
					html === '<p></p>';

				textSet.current = true;
				onChange(isEmpty ? '' : html);
			});

			editorRef.current = quill;
		}, []);

		useEffect(() => {
			if (!editorRef.current) return;

			// When value is cleared (e.g. form reset on success), clear the editor
			if (!value || value === '') {
				editorRef.current.root.innerHTML = '<p><br></p>';
				textSet.current = false;
				return;
			}

			// Only set from external value when we haven't just typed (user-driven)
			if (!textSet.current) {
				editorRef.current.root.innerHTML = value;
				textSet.current = true;
			}
		}, [value]);

		return (
			<FormControl sx={containerStyle} {...rest}>
				<OnErrorScrollPoint name={rest.name} />
				{label && <InputLabel {...LabelProps}>{label}</InputLabel>}
				<Box
					sx={editorContentStyle}
					className={classnames({
						'Mui-error': rest.error,
						'Mui-disabled': rest.disabled,
						'Mui-focused': meta.active,
					})}
				>
					<div id={editorId} className='quill' />
				</Box>
				<Box id={`${editorId}-toolbar`}>
					<button className='ql-bold' type='button' />
					<button className='ql-italic' type='button' />
					<button className='ql-strike' type='button' />
					<button className='ql-underline' type='button' />
					<button className='ql-link' type='button' />
					<button className='ql-list' value='bullet' type='button' />
					<button className='ql-list' value='ordered' type='button' />
				</Box>
				{helperText && <FormHelperText>{helperText}</FormHelperText>}
			</FormControl>
		);
	}
);
